<template>
  <div class="gap-4 md:flex">
    <div class="relative md:w-1/2">
      <base-picture
        :alt="productSet.text"
        class="w-full"
        :height="size"
        :src="getResponsiveImageTransformations(productSet.images[0], { size })"
        :width="size"
      />
      <div
        v-style:top="{ sm: '36%', md: '24%' }"
        class="absolute flex center gap-1 rounded-full bg-white px-2 py-3 -translate-1/2"
        style="left: 40%"
      >
        <vf-icon name="bag" />
        <span class="text-sm">{{ productSet.products?.length }}</span>
      </div>
      <div class="absolute bottom-0 w-full flex p-4 between">
        <h2 class="title-2">
          {{ productSet.description }}
        </h2>
        <vf-button
          :aria-label="allFavorited.length === productSet.products?.length ? $t.removeFromFavorites : $t.favorites"
          class="b-0 rounded-full p-1"
          :disabled="!productSet.products?.length || error"
          :loading="pending"
          variant="secondary"
          @click="toggleFavorite"
        >
          <transition name="fade">
            <vf-icon
              :class="{ 'c-brand-4': allFavorited.length !== productSet.products?.length }"
              :name="allFavorited.length === productSet.products?.length ? 'heart-filled' : 'heart'"
              size="md"
            />
          </transition>
        </vf-button>
      </div>
    </div>
    <div class="relative <md:mt-6 md:w-1/2">
      <div class="overflow-x-auto md:absolute-0">
        <div class="grid gap-2 <md:grid-flow-col md:cols-3 md:px-6">
          <template v-for="product in products" :key="product.id">
            <transition appear name="fade">
              <product-shop-the-look-card v-style:w="{ sm: '40vw', md: 'auto' }" :product />
            </transition>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ProductExtended, ProductSet } from '#types/product'

const { productSet } = defineProps<{
  productSet: ProductSet
}>()

const { add, error, favorites, remove } = useFavoritesStore()
const { $t } = useNuxtApp()
const toast = useToaster()

const pending = ref(false)
const size = { sm: 360, md: 696 }

const favoriteIds = computed(() => new Set(favorites.map(({ id }) => id)))
const allFavorited = computed(() => productSet.products?.filter(({ id }) => favoriteIds.value.has(id)) ?? [])

const promiseData = await Promise.all((productSet.products ?? [])
  .map(({ id }) => useProduct(id, { server: false, key: `product-card-${id}` })))

const products = computed(() => promiseData.filter(({ productPending }) => !productPending.value)
  .map(({ product }) => product.value as ProductExtended))

const toggleFavorite = async () => {
  // Store the initial favorite state before it changes during the loop
  const isFavoriteRef = allFavorited.value.length === productSet.products?.length

  pending.value = true

  for (const { id, name } of products.value) {
    const isFavorite = favoriteIds.value.has(id)

    try {
      if (isFavoriteRef && isFavorite) {
        await remove(id)
        toast.add({
          props: {
            type: 'success',
            message: replaceAll($t.favoritesRemoved, { name }),
            ctas: [{
              label: $t.viewFavorites,
              to: '/favorites'
            }]
          }
        })
      }
      else {
        await add(id)
        toast.add({
          props: {
            type: 'success',
            message: replaceAll($t.favoritesAdded, { name }),
            ctas: [{
              label: $t.viewFavorites,
              to: '/favorites'
            }]
          }
        })
      }
    }
    catch {
      toast.add({
        props: {
          type: 'error',
          title: $t.somethingWentWrong,
        }
      })
    }
    finally {
      pending.value = false
    }
  }
}
</script>
